import type { ButtonHTMLAttributes } from 'react';

import type { IconMapping } from '@onyx/ui/components/colleague/icon';
import { Icon } from '@onyx/ui/components/colleague/icon';
import { getClassName } from '@onyx/ui/style-utils';

import styles from './icon-button.module.css';

export type IconButtonVariant =
  | 'filled'
  | 'outlined'
  | 'standard'
  | 'destructive';

export type IconButtonProps = Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  | 'id'
  | 'onClick'
  | 'onFocus'
  | 'onBlur'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'aria-haspopup'
  | 'aria-expanded'
> & {
  /**
   * Required. The button renders nothing but a decorative icon, so this is the
   * only source of an accessible name. See the icon accessibility contract in
   * `libs/ui/components/colleague/svg/README.md`.
   */
  'aria-label': string;
  icon: IconMapping;
  isDisabled?: boolean;
  variant?: IconButtonVariant;
  isInverse?: boolean;
};

export const IconButton = ({
  icon,
  variant = 'standard',
  isDisabled = false,
  isInverse = false,
  onClick,
  ...rest
}: IconButtonProps) => {
  const variantMap: Record<IconButtonVariant, string> = {
    filled: styles.variantFilled,
    outlined: styles.variantOutlined,
    standard: styles.variantStandard,
    destructive: styles.variantDestructive,
  };

  const isInverseAllowed = variant !== 'destructive' && isInverse;

  const classNames = getClassName([
    styles.iconButton,
    variantMap[variant],
    isInverseAllowed && styles.inverse,
  ]);

  return (
    <button
      type="button"
      className={classNames}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      {...rest}
    >
      <div className={styles.iconWrapper}>
        <Icon name={icon} />
      </div>
    </button>
  );
};
